import { match, P } from 'ts-pattern';
import {
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type {
  IxmUserauthV1BrandingSpec,
  IxmUserauthV1BrandingVersion,
  IxmUserauthV1ClearingDetails,
  IxmUserauthV1Color,
  IxmUserauthV1Colors,
  IxmUserauthV1ContactDetails,
  IxmUserauthV1CustomerSupportContacts,
  IxmUserauthV1FontType,
  IxmUserauthV1FontWeight,
  IxmUserauthV1GoogleFont,
  IxmUserauthV1GoogleFontAxes,
  IxmUserauthV1ProgramContacts,
  IxmUserauthV1ProgramControls,
  IxmUserauthV1ProgramControlsApiAuth,
  IxmUserauthV1ProgramControlsApiAuthLimits,
  IxmUserauthV1ProgramControlsApiAuthSettings,
  IxmUserauthV1ProgramData,
  IxmUserauthV1ProgramDetails,
  IxmUserauthV1ProgramRegistrationType,
  IxmUserauthV1ProgramRegistrationTypeBrokerDealer,
  IxmUserauthV1ProgramRegistrationTypeInvestmentAdviser,
  IxmUserauthV1ProgramSpec,
  IxmUserauthV1ProgramState,
  IxmUserauthV1Vendors,
} from '@interxfi/types/generated/api';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { getBooleanFromUndefinedOrNullable } from '@interxfi/utilities/object/access';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import {
  getFundingAccountNumbersControlsListItems,
  getFundingBatchControlsListItems,
  getFundingControlsListItems,
} from '../../funding/source/funding-source-list-items';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getBatchOrderControlsListItems, getOrderControlsListItems } from '../../order/order-list-items';
import {
  getPortfolioBooksControlsListItems,
  getPortfolioInstantDepositControlsListItems,
  getPortfolioSweepsControlsListItems,
} from '../../portfolio/shared-portfolio-list-items';
import { getRegistrationAddressListItems, getRegistrationClearingControlsListItems } from '../../registration/shared-registration-list-items';
import { ProgramStatusPill } from './program-status-pill';

export function getFontWeightListItems(data: IxmUserauthV1FontWeight | undefined, prefix = '') {
  return {
    regular: getReadonlyListItemNumber(`${prefix}regular`, 'Regular', data?.regular),
    medium: getReadonlyListItemNumber(`${prefix}medium`, 'Medium', data?.medium),
    bold: getReadonlyListItemNumber(`${prefix}bold`, 'Bold', data?.bold),
  } as const satisfies ReadonlyListItemRecord;
}

export function getGoogleFontAxesListItems(data: IxmUserauthV1GoogleFontAxes | undefined, prefix = '') {
  return {
    ital: getReadonlyListItemYesOrNo(`${prefix}ital`, 'Italic', getBooleanFromUndefinedOrNullable(data, 'ital')),
    opsz: getReadonlyListItemNumber(`${prefix}opsz`, 'Optical Size', data?.opsz),
    slnt: getReadonlyListItemNumber(`${prefix}slnt`, 'Slant', data?.slnt),
    wdth: getReadonlyListItemNumber(`${prefix}wdth`, 'Width', data?.wdth),
  };
}

export function getGoogleFontListItems(data: IxmUserauthV1GoogleFont | undefined, prefix = '') {
  return {
    family: getReadonlyListItemString(`${prefix}family`, 'Family', data?.family),
    axes: getGoogleFontAxesListItems(data?.axes, `${prefix}axes.`),
    weights: getFontWeightListItems(data?.weights, `${prefix}weights.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramFontTypeListItems(data: IxmUserauthV1FontType | undefined, prefix = '') {
  const typeNameItem = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixmUserauthV1',
      oneOfName: 'IxmUserauthV1FontType',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameItem;
  }

  if ('googleFont' in data) {
    return {
      ...typeNameItem,
      googleFont: {
        '!type': 'googleFont',
        value: getGoogleFontListItems(data.googleFont, `${prefix}googleFont.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('customFont' in data) {
    return {
      ...typeNameItem,
      customFont: {
        '!type': 'customFont',
        value: {
          artifactId: getReadonlyListItemId(`${prefix}customFont.artifactId`, 'Artifact ID', data.customFont.artifactId),
          family: getReadonlyListItemString(`${prefix}customFont.family`, 'Family', data.customFont.family),
          weights: getFontWeightListItems(data.customFont.weights, `${prefix}customFont.weights.`),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameItem;
}

export function getProgramBrandingColorListItems(data: IxmUserauthV1Color | undefined, prefix = '') {
  return {
    standardColor: getReadonlyListItemString(
      `${prefix}standardColor`,
      'Standard Color',
      match(data?.standardColor)
        .with({ hex: P.not(P.nullish) }, ({ hex }) => hex)
        .with({ hsla: P.not(P.nullish) }, ({ hsla }) => `hsla(${hsla.h}, ${hsla.s}, ${hsla.l}, ${hsla.a})`)
        .with({ rgba: P.not(P.nullish) }, ({ rgba }) => `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`)
        .otherwise(() => undefined),
    ),
    wideGamutColor: getReadonlyListItemString(
      `${prefix}wideGamutColor`,
      'Wide-Gamut Color',
      match(data?.wideGamutColor)
        .with({ displayP3: P.not(P.nullish) }, ({ displayP3 }) => `color(display-p3 ${displayP3.r} ${displayP3.g} ${displayP3.b} / ${displayP3.a})`)
        .otherwise(() => undefined),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramBrandingColorsListItems(data: IxmUserauthV1Colors | undefined, prefix = '') {
  return {
    background: getProgramBrandingColorListItems(data?.background, `${prefix}background`),
    separator: getProgramBrandingColorListItems(data?.separator, `${prefix}separator`),
    textLight: getProgramBrandingColorListItems(data?.textLight, `${prefix}textLight`),
    textMedium: getProgramBrandingColorListItems(data?.textMedium, `${prefix}textMedium`),
    textButton: getProgramBrandingColorListItems(data?.textButton, `${prefix}textButton`),
    neutralExtraLight: getProgramBrandingColorListItems(data?.neutralExtraLight, `${prefix}neutralExtraLight`),
    neutralLight: getProgramBrandingColorListItems(data?.neutralLight, `${prefix}neutralLight`),
    neutralMedium: getProgramBrandingColorListItems(data?.neutralMedium, `${prefix}neutralMedium`),
    neutralDark: getProgramBrandingColorListItems(data?.neutralDark, `${prefix}neutralDark`),
    primaryLight: getProgramBrandingColorListItems(data?.primaryLight, `${prefix}primaryLight`),
    primaryMedium: getProgramBrandingColorListItems(data?.primaryMedium, `${prefix}primaryMedium`),
    primaryDark: getProgramBrandingColorListItems(data?.primaryDark, `${prefix}primaryDark`),
    secondaryLight: getProgramBrandingColorListItems(data?.secondaryLight, `${prefix}secondaryLight`),
    secondaryMedium: getProgramBrandingColorListItems(data?.secondaryMedium, `${prefix}secondaryMedium`),
    secondaryDark: getProgramBrandingColorListItems(data?.secondaryDark, `${prefix}secondaryDark`),
    tertiaryLight: getProgramBrandingColorListItems(data?.tertiaryLight, `${prefix}tertiaryLight`),
    tertiaryMedium: getProgramBrandingColorListItems(data?.tertiaryMedium, `${prefix}tertiaryMedium`),
    tertiaryDark: getProgramBrandingColorListItems(data?.tertiaryDark, `${prefix}tertiaryDark`),
    quaternaryLight: getProgramBrandingColorListItems(data?.quaternaryLight, `${prefix}quaternaryLight`),
    quaternaryMedium: getProgramBrandingColorListItems(data?.quaternaryMedium, `${prefix}quaternaryMedium`),
    quaternaryDark: getProgramBrandingColorListItems(data?.quaternaryDark, `${prefix}quaternaryDark`),
    successLight: getProgramBrandingColorListItems(data?.successLight, `${prefix}successLight`),
    successMedium: getProgramBrandingColorListItems(data?.successMedium, `${prefix}successMedium`),
    successDark: getProgramBrandingColorListItems(data?.successDark, `${prefix}successDark`),
    greenLight: getProgramBrandingColorListItems(data?.greenLight, `${prefix}greenLight`),
    greenMedium: getProgramBrandingColorListItems(data?.greenMedium, `${prefix}greenMedium`),
    greenDark: getProgramBrandingColorListItems(data?.greenDark, `${prefix}greenDark`),
    dangerLight: getProgramBrandingColorListItems(data?.dangerLight, `${prefix}dangerLight`),
    dangerMedium: getProgramBrandingColorListItems(data?.dangerMedium, `${prefix}dangerMedium`),
    dangerDark: getProgramBrandingColorListItems(data?.dangerDark, `${prefix}dangerDark`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramBrandingSpecListItems(data: IxmUserauthV1BrandingSpec | undefined, prefix = '') {
  return {
    logoArtifactId: data?.logoArtifactId ? getReadonlyListItemId(`${prefix}logoArtifactId`, 'Logo Artifact ID', data.logoArtifactId) : undefined,
    logoMarkArtifactId: data?.logoMarkArtifactId
      ? getReadonlyListItemId(`${prefix}logoMarkArtifactId`, 'Logomark Artifact ID', data.logoMarkArtifactId)
      : undefined,
    colors: getProgramBrandingColorsListItems(data?.colors, `${prefix}colors`),
    mainFont: getProgramFontTypeListItems(data?.mainFont, `${prefix}mainFont`),
    monoFont: getProgramFontTypeListItems(data?.monoFont, `${prefix}monoFont`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramBrandingVersionListItems(data: IxmUserauthV1BrandingVersion | undefined, prefix = '') {
  return {
    brandingVersionId: getReadonlyListItemId(`${prefix}brandingVersionId`, 'Branding Version ID', data?.brandingVersionId),
    createdAt: getReadonlyListItemDate(`${prefix}createdAt`, 'Created At', data?.createdAt, 'timestamp_iso_s'),
    branding: getProgramBrandingSpecListItems(data?.branding, `${prefix}branding.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramContactListItems(data: IxmUserauthV1ContactDetails | undefined, prefix = '') {
  return {
    name: getReadonlyListItemString(`${prefix}name`, 'Name', data?.name),
    title: getReadonlyListItemString(`${prefix}title`, 'Title', data?.title),
    nameAndTitle: getReadonlyListItemString(`${prefix}nameAndTitle`, 'Name & Job Title', [data?.name, data?.title].filter(Boolean).join(', ')),
    email: getReadonlyListItemString(`${prefix}email`, 'Email Address', data?.email),
    phone: getReadonlyListItemString(`${prefix}phone`, 'Phone', data?.phone),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramContactsListItems(data: IxmUserauthV1ProgramContacts | undefined, prefix = '') {
  return {
    executive: data?.executive?.map((contact, i) => getProgramContactListItems(contact, `${prefix}executive.${i}.`)),
    technical: data?.technical?.map((contact, i) => getProgramContactListItems(contact, `${prefix}technical.${i}.`)),
    billing: data?.billing?.map((contact, i) => getProgramContactListItems(contact, `${prefix}billing.${i}.`)),
    operations: data?.operations?.map((contact, i) => getProgramContactListItems(contact, `${prefix}operations.${i}.`)),
    compliance: data?.compliance?.map((contact, i) => getProgramContactListItems(contact, `${prefix}compliance.${i}.`)),
    disasterRecovery: data?.disasterRecovery?.map((contact, i) => getProgramContactListItems(contact, `${prefix}disasterRecovery.${i}.`)),
    aml: data?.aml?.map((contact, i) => getProgramContactListItems(contact, `${prefix}aml.${i}.`)),
    cat: data?.cat?.map((contact, i) => getProgramContactListItems(contact, `${prefix}cat.${i}.`)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramRegistrationTypeBrokerDealerListItems(data: IxmUserauthV1ProgramRegistrationTypeBrokerDealer | undefined, prefix = '') {
  return {
    crdNumber: getReadonlyListItemString(`${prefix}crdNumber`, 'CRD Number', data?.crdNumber),
    secRegistrationNumber: getReadonlyListItemString(`${prefix}secRegistrationNumber`, 'SEC Registration Number', data?.secRegistrationNumber),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramRegistrationTypeInvestmentAdviserListItems(
  data: IxmUserauthV1ProgramRegistrationTypeInvestmentAdviser | undefined,
  prefix = '',
) {
  return {
    crdNumber: getReadonlyListItemString(`${prefix}crdNumber`, 'CRD Number', data?.crdNumber),
    secRegistrationNumber: getReadonlyListItemString(`${prefix}secRegistrationNumber`, 'SEC Registration Number', data?.secRegistrationNumber),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramRegistrationTypeListItems(data: IxmUserauthV1ProgramRegistrationType | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixmUserauthV1',
      oneOfName: 'IxmUserauthV1ProgramRegistrationType',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('brokerDealer' in data) {
    return {
      ...typeNameColumn,
      brokerDealer: {
        '!type': 'brokerDealer',
        value: getProgramRegistrationTypeBrokerDealerListItems(data.brokerDealer, `${prefix}brokerDealer.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('investmentAdviser' in data) {
    return {
      ...typeNameColumn,
      investmentAdviser: {
        '!type': 'investmentAdviser',
        value: getProgramRegistrationTypeInvestmentAdviserListItems(data.investmentAdviser, `${prefix}investmentAdviser.`),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('none' in data) {
    return {
      ...typeNameColumn,
      none: {
        '!type': 'none',
        value: {},
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getProgramVendorsListItems(data: IxmUserauthV1Vendors | undefined, prefix = '') {
  return {
    cipIdentity: getReadonlyListItemTranslatedString(`${prefix}cipIdentity`, 'CIP Identity', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1CipVendor',
      value: data?.cipIdentity,
    }),
    cipOfac: getReadonlyListItemTranslatedString(`${prefix}cipOfac`, 'CIP OFAC', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1CipOfac',
      value: data?.cipOfac,
    }),
    bankAccountValidation: getReadonlyListItemTranslatedString(`${prefix}bankAccountValidation`, 'Bank Account Validation', {
      namespace: 'ixmUserauthV1',
      enumName: 'IxmUserauthV1BankAccountValidation',
      value: data?.bankAccountValidation,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramCustomerSupportContactsListItems(data: IxmUserauthV1CustomerSupportContacts | undefined, prefix = '') {
  return {
    displayName: getReadonlyListItemString(`${prefix}displayName`, 'Program Display Name', data?.displayName),
    website: getReadonlyListItemString(`${prefix}website`, 'Website', data?.website),
    emailAddress: getReadonlyListItemString(`${prefix}emailAddress`, 'Email Address', data?.emailAddress),
    phone: getReadonlyListItemString(`${prefix}phone`, 'Phone', data?.phone),
    phoneTaxDocuments: getReadonlyListItemString(`${prefix}phoneTaxDocuments`, 'Phone (Tax Forms)', data?.phoneTaxDocuments),
    mailingAddress: getRegistrationAddressListItems(data?.mailingAddress, `${prefix}mailingAddress.`, 'Mailing Address'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramDetailsListItems(data: IxmUserauthV1ProgramDetails | undefined, prefix = '') {
  return {
    registrationType: getProgramRegistrationTypeListItems(data?.registrationType, `${prefix}registrationType.`),
    name: getReadonlyListItemString(`${prefix}name`, 'Name', data?.name),
    legalName: getReadonlyListItemString(`${prefix}legalName`, 'Legal Name', data?.legalName),
    dba: getReadonlyListItemString(`${prefix}dba`, 'DBA', data?.dba),
    webDomains: getReadonlyListItemString(`${prefix}webDomains`, 'Web Domains', data?.webDomains?.join('\n')),
    taxIdentifier: getReadonlyListItemString(`${prefix}taxIdentifier`, 'Tax ID / EIN', data?.taxIdentifier),
    mainOfficeAddress: getRegistrationAddressListItems(data?.mainOfficeAddress, `${prefix}mainOfficeAddress.`, 'Main Office Address'),
    mailingAddress: getRegistrationAddressListItems(data?.mailingAddress, `${prefix}mailingAddress.`, 'Mailing Address'),
    customerSupportContacts: getProgramCustomerSupportContactsListItems(data?.customerSupportContacts, `${prefix}customerSupportContacts.`),
    vendors: getProgramVendorsListItems(data?.vendors, `${prefix}vendors.`),
    contacts: getProgramContactsListItems(data?.contacts, `${prefix}contacts.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramApiAuthControlsLimitsListItems(data: IxmUserauthV1ProgramControlsApiAuthLimits | undefined, prefix = '') {
  return {
    apiKeysMax: getReadonlyListItemNumber(`${prefix}apiKeysMax`, 'Max API Keys', data?.apiKeysMax),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramApiAuthControlsSettingsListItems(data: IxmUserauthV1ProgramControlsApiAuthSettings | undefined, prefix = '') {
  return {
    canCreateApiKeys: getReadonlyListItemYesOrNo(`${prefix}canCreateApiKeys`, 'Create API Keys', Boolean(data?.canCreateApiKeys)),
    canGetToken: getReadonlyListItemYesOrNo(`${prefix}canGetToken`, 'Get API Token', Boolean(data?.canGetToken)),
    canRevokeApiKeys: getReadonlyListItemYesOrNo(`${prefix}canRevokeApiKeys`, 'Revoke API Keys', Boolean(data?.canRevokeApiKeys)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramApiAuthControlsListItems(data: IxmUserauthV1ProgramControlsApiAuth | undefined, prefix = '') {
  return {
    limits: getProgramApiAuthControlsLimitsListItems(data?.limits, `${prefix}limits.`),
    settings: getProgramApiAuthControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramControlsListItems(data: IxmUserauthV1ProgramControls | undefined, prefix = '') {
  return {
    apiAuth: getProgramApiAuthControlsListItems(data?.apiAuth, `${prefix}apiAuth.`),
    funding: getFundingControlsListItems(data?.funding, `${prefix}funding.`),
    batchFunding: getFundingBatchControlsListItems(data?.batchFunding, `${prefix}batchFunding.`),
    books: getPortfolioBooksControlsListItems(data?.books, `${prefix}books.`),
    instantDeposit: getPortfolioInstantDepositControlsListItems(data?.instantDeposit, `${prefix}instantDeposit.`),
    accountNumbers: getFundingAccountNumbersControlsListItems(data?.accountNumbers, `${prefix}accountNumbers.`),
    order: getOrderControlsListItems(data?.order, `${prefix}order.`),
    batchOrder: getBatchOrderControlsListItems(data?.batchOrder, `${prefix}batchOrder.`),
    registration: getRegistrationClearingControlsListItems(data?.registration, `${prefix}registration.`),
    sweeps: getPortfolioSweepsControlsListItems(data?.sweeps, `${prefix}sweeps.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramClearingDetailsListItems(data: IxmUserauthV1ClearingDetails | undefined, prefix = '') {
  return {
    shortName: getReadonlyListItemString(`${prefix}shortName`, 'Short Name', data?.shortName),
    mpid: getReadonlyListItemString(`${prefix}mpid`, 'MPID', data?.mpid),
    taxVendorId: getReadonlyListItemString(`${prefix}taxVendorId`, 'Tax Vendor ID', data?.taxVendorId),
    proxyVendorId: getReadonlyListItemString(`${prefix}proxyVendorId`, 'Proxy Vendor ID', data?.proxyVendorId),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramSpecListItems(data: IxmUserauthV1ProgramSpec | undefined, prefix = '') {
  return {
    controls: getProgramControlsListItems(data?.controls, `${prefix}controls.`),
    details: getProgramDetailsListItems(data?.details, `${prefix}details.`),
    clearingDetails: getProgramClearingDetailsListItems(data?.clearingDetails, `${prefix}clearingDetails.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramDataListItems(data: IxmUserauthV1ProgramData | undefined, prefix = '') {
  return {
    ...getProgramSpecListItems(data, prefix),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramStateListItems(data: IxmUserauthV1ProgramState | undefined) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    programId: getReadonlyListItemId('programId', 'Program ID', data?.programId),
    programCode: getReadonlyListItemString('programCode', 'Program Code', data?.programCode),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixmUserauthV1',
      enumName: 'IxmUserauthV1ProgramStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <ProgramStatusPill status={data.status} /> : null),
    data: getProgramDataListItems(data?.data, 'data.'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getProgramDetailListItemSummarySections(items: ReturnType<typeof getProgramDetailsListItems>): ReadonlyListGroup[] {
  return [
    {
      id: 'programInformation',
      title: 'Program Information',
      items: [items.name, { ...items.registrationType.typeName, title: 'Registration Type' }],
    },
    {
      id: 'correspondentInformation',
      title: 'Correspondent Information',
      items: [
        items.legalName,
        items.webDomains,
        items.dba,
        items.taxIdentifier,
        ...match(items.registrationType)
          .with({ brokerDealer: P.not(P.nullish) }, (r) => [r.brokerDealer.value.crdNumber, r.brokerDealer.value.secRegistrationNumber])
          .with({ investmentAdviser: P.not(P.nullish) }, (r) => [
            r.investmentAdviser.value.crdNumber,
            r.investmentAdviser.value.secRegistrationNumber,
          ])
          .with({ none: P.not(P.nullish) }, () => [])
          .otherwise(() => []),
        items.mainOfficeAddress.formattedMultiLine,
        items.mailingAddress.formattedMultiLine,
      ],
    },
    {
      id: 'customerSupportContact',
      title: 'Customer Support Contact',
      items: [
        items.customerSupportContacts.displayName,
        items.customerSupportContacts.website,
        items.customerSupportContacts.emailAddress,
        items.customerSupportContacts.phone,
        items.customerSupportContacts.phoneTaxDocuments,
        items.customerSupportContacts.mailingAddress.formattedMultiLine,
      ],
    },
    {
      id: 'vendors',
      title: 'Correspondent Vendors',
      items: [items.vendors.cipIdentity, items.vendors.cipOfac, items.vendors.bankAccountValidation],
    },
  ];
}
